import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/shared/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "cyber-overlay fixed inset-0 z-50 backdrop-blur-md",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "duration-300",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "cyber-dialog-title text-lg font-semibold leading-none tracking-wider uppercase",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

/**
 * 递归在 children 树中找 DialogDescription;若无,我们让 DialogContent 不输出
 * `aria-describedby` 属性,以抑制 Radix 的 "Missing Description" 警告。
 *
 * Radix v1 react-dialog DescriptionWarning 的判定:Content 渲染后若 DOM
 * 上 aria-describedby 指向的 id 不存在 → warn。把属性设成 undefined
 * 让 React 不输出该属性 → 跳过判定。
 */
function hasDialogDescription(children: React.ReactNode): boolean {
  let found = false;
  React.Children.forEach(children, (child) => {
    if (found || !React.isValidElement(child)) return;
    if (child.type === DialogDescription) {
      found = true;
      return;
    }
    const nested = (child.props as { children?: React.ReactNode } | null)?.children;
    if (nested) {
      found = found || hasDialogDescription(nested);
    }
  });
  return found;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, "aria-describedby": ariaDescribedBy, ...props }, ref) => {
  // 调用方传了 aria-describedby → 用调用方的值
  // 调用方没传 + 子树有 DialogDescription → 不传(让 Radix 用 context.descriptionId 自动接管)
  // 调用方没传 + 子树没 DialogDescription → 显式 undefined,React 不输出属性 → 抑制 Radix 警告
  const hasExplicit = ariaDescribedBy !== undefined;
  const ariaProps = hasExplicit
    ? { "aria-describedby": ariaDescribedBy }
    : hasDialogDescription(children)
      ? {} // 不覆盖 Radix 的默认 descriptionId
      : { "aria-describedby": undefined };
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        {...ariaProps}
        className={cn(
          // ★2026-07-29:高度封顶 + 三段式(头/可滚正文/尾)。原来是 `grid` 且**无 max-height
          // 无 overflow** —— 内容一超屏,fixed+translate-y(-50%) 让弹窗上下同时溢出视口,而
          // Radix 打开时给 body 上了 overflow:hidden,页面也滚不动 → 提交按钮永远够不着。
          // 测试实证(知识库上传):40+ 个系统标签把高度顶爆,「划不动、点不了上传按钮」。
          //
          // ★为什么不是直接给 Content 加 overflow-y-auto:那样会把四角 cyber-corner 装饰和
          //   扫描线一起裁掉,内部 sticky 也会以 Content 为滚动容器而失效。改用 flex 三段式,
          //   只让**正文**滚(正文由调用方用 DialogBody 包,未包的老弹窗行为不变)。
          // ★2026-08-18(DH-PICK-1):这里的 `fixed` 是弹窗待在视口内的唯一支柱,而调用方
          //   className 里任何 position utility(relative/absolute/…)都会被 tailwind-merge
          //   按同组规则**安静地顶掉它** —— 弹窗顺流排进文档流,portal 在 body 尾部 = 排到页面
          //   底部视口外,用户只见遮罩。DhAvatarPicker 实测中招。调用方永远不该改模态的
          //   position;全仓锁见 dialog.position-lock.test.ts。
          "cyber-dialog fixed left-[50%] top-[50%] z-50 flex w-full max-w-lg translate-x-[-50%] translate-y-[-50%]",
          "max-h-[85dvh] flex-col p-6 sm:rounded-xl",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-90 data-[state=open]:zoom-in-90",
          "data-[state=closed]:slide-out-to-top-[52%] data-[state=open]:slide-in-from-top-[52%]",
          "data-[state=open]:duration-500 data-[state=closed]:duration-200",
          className
        )}
        {...props}
      >
        {/* HUD corner brackets */}
        <span aria-hidden className="cyber-corner cyber-corner-tl absolute" />
        <span aria-hidden className="cyber-corner cyber-corner-tr absolute" />
        <span aria-hidden className="cyber-corner cyber-corner-bl absolute" />
        <span aria-hidden className="cyber-corner cyber-corner-br absolute" />
        {/* Scan line that sweeps on open */}
        <span aria-hidden className="cyber-scan-sweep" />
        {/*
          内容包一层滚动壳:内容超过 85dvh 时**这一层**滚,而不是让整个弹窗溢出视口。
          - 装饰四角/扫描线/关闭键是它的**兄弟**,留在 Content 上 → 不随内容滚走;
          - `min-h-0` 必须有:flex 子项默认 min-height:auto,不置 0 的话它不肯收缩,
            overflow 永远不触发(这行没了 = 这个修复整个失效);
          - `gap-4` 从 Content 挪到这里 —— 原来 Content 是 grid 靠 gap 排children,
            现在 children 全在这一层里,间距得跟着走,否则每个弹窗的头/体/尾会挤成一坨。
        */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">{children}</div>
        <DialogPrimitive.Close
          className={cn(
            // 配色走 token;cyber 霓虹 hover 由 skin-cyber.css `.cyber-dialog-close` 覆写
            // ★z-20(2026-08-05 测试环境实拍):长图预览把整幅图铺进弹窗且给了 z-10,
            //   关闭键原来是 z:auto,滚到有画面的那一屏就**被图盖住** ——
            //   hit-test 打在 <img> 上,视觉上白底白键也看不见,用户只能按 Esc。
            //   判据钉在「弹窗里任何内容都不许盖住关闭键」,不是比谁的 z 大(那条学费在 root z-index)。
            "cyber-dialog-close absolute right-4 top-4 z-20 rounded-full p-1.5 text-muted-foreground",
            // ★不透明底 + 阴影:半透明底压在深色画面上等于隐形(同一次实拍)
            "border border-border bg-background shadow-md",
            "transition-all duration-200",
            "hover:text-foreground hover:border-ring hover:rotate-90",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:pointer-events-none"
          )}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">关闭</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      "border-b border-border pb-4",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      "border-t border-border pt-4",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
